import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { Subscription } from "rxjs";
import PhoneControl from "../../../../models/company-resources/phone-control";
import BusinessPhoneNumber, {
  BusinessPhoneNumberType,
} from "../../../../models/contact/business-phone-number";
import { commonVar } from "../../../../providers/common-var/common";

const PHONE_NUMBERS_CONTROL = "phoneNumbers";

@Component({
  selector: "workforce-phone-info",
  templateUrl: "workforce-phone-info.html",
})
export class WorkforcePhoneInfoComponent implements AfterViewInit, OnDestroy {
  phoneMask = commonVar.PHONE_US_MASK;
  @Input() formGroup: FormGroup;
  @Input() phoneNumberControl: PhoneControl;
  @Output() emitterAddPhoneNumber = new EventEmitter<BusinessPhoneNumberType>();
  @Input() isEditMode: any;

  subscriptions: Subscription[] = [];

  constructor(protected formBuilder: FormBuilder) {}

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.currentPhoneNumbers.valueChanges.subscribe((value) => {
        this.updatePhoneNumbersControl();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  removePhoneNumber(index, selectedNumber) {
    selectedNumber.get("deleted").setValue(true);
    if (selectedNumber.value["id"] > 0) {
      selectedNumber.get("phoneNumber").clearValidators();
      selectedNumber.get("phoneNumberType").clearValidators();
      this.deletedPhoneNumbers.push(
        this.createController(selectedNumber.value)
      );
    }
    this.updatePrimary(selectedNumber.value);
  }

  private updatePrimary(phoneNumber: BusinessPhoneNumber) {
    if (
      phoneNumber &&
      phoneNumber.primary &&
      this.notDeletedControls().length > 0
    ) {
      this.toggleRadio(this.notDeletedControls()[0], 0);
    }
  }

  addPhoneNumber() {
    if (this.currentPhoneNumbers && !this.verifyLengthLessThanFour()) return;
    let phone = this.createNewPhoneNumber();
    if (this.notDeletedControls().length === 0) {
      phone.primary = true;
    }
    this.currentPhoneNumbers.push(this.createController(phone, []));
  }

  private notDeletedControls() {
    return this.currentPhoneNumbers.controls.filter((x) => !x.value.deleted);
  }

  get currentPhoneNumbers() {
    return this.formGroup.controls[
      this.phoneNumberControl.control
    ] as FormArray;
  }

  get phoneNumbers() {
    return this.formGroup.get(PHONE_NUMBERS_CONTROL) as FormArray;
  }

  get deletedPhoneNumbers() {
    return this.formGroup.get("deletedPhoneNumbers") as FormArray;
  }

  updatePhoneNumbersControl() {
    if (this.formGroup.contains(PHONE_NUMBERS_CONTROL)) {
      this.formGroup.get(PHONE_NUMBERS_CONTROL).clearValidators();
      this.formGroup.removeControl(PHONE_NUMBERS_CONTROL);
    }
    this.formGroup.addControl(PHONE_NUMBERS_CONTROL, new FormArray([]));

    let workPhoneNumbers = this.verifyFormat(
      this.formGroup.get("workPhoneNumbers").value
    )
      .map((x) => {
        return {
          ...x,
          phoneNumberType: BusinessPhoneNumberType.WORK,
          primary: !!x.primary,
        };
      })
      .filter((x) => !x.deleted);
    let faxPhoneNumbers = this.verifyFormat(
      this.formGroup.get("faxPhoneNumbers").value
    )
      .map((x) => {
        return {
          ...x,
          phoneNumberType: BusinessPhoneNumberType.FAX,
          primary: !!x.primary,
        };
      })
      .filter((x) => !x.deleted);
    let cellPhoneNumbers = this.verifyFormat(
      this.formGroup.get("cellPhoneNumbers").value
    )
      .map((x) => {
        return {
          ...x,
          phoneNumberType: BusinessPhoneNumberType.CELL,
          primary: !!x.primary,
        };
      })
      .filter((x) => !x.deleted);

    let allPhoneNumbers = []
      .concat(workPhoneNumbers)
      .concat(faxPhoneNumbers)
      .concat(cellPhoneNumbers);

    const allEmpty =
      workPhoneNumbers.length === 0 &&
      faxPhoneNumbers.length === 0 &&
      cellPhoneNumbers.length === 0;

    if (allEmpty) {
      this.verifyPhoneNumbersLength(allPhoneNumbers);
    }

    allPhoneNumbers.forEach((phoneNumber) => {
      const group = this.createController(phoneNumber);
      this.phoneNumbers.push(group);
    });

    this.patchValues(allPhoneNumbers);
  }

  private verifyPhoneNumbersLength(allPhoneNumbers: BusinessPhoneNumber[]) {
    const phone = this.createNewPhoneNumber();
    allPhoneNumbers.push(phone);
    this.phoneNumbers.push(this.createController(phone));
  }

  private createNewPhoneNumber() {
    return {
      id: null,
      phoneNumber: null,
      phoneNumberExtension: "",
      phoneNumberType: null,
      deleted: false,
      primary: false,
    } as BusinessPhoneNumber;
  }

  private createController(
    phoneNumber: BusinessPhoneNumber,
    validators: Validators[] = [Validators.required, Validators.minLength(13)]
  ) {
    return this.formBuilder.group({
      id: [phoneNumber.id],
      phoneNumber: [phoneNumber.phoneNumber, validators],
      phoneNumberExtension: [
        phoneNumber.phoneNumberExtension
          ? phoneNumber.phoneNumberExtension
          : "",
      ],
      phoneNumberType: [this.phoneNumberControl.phoneType, Validators.required],
      deleted: [phoneNumber.deleted],
      primary: [phoneNumber.primary],
    });
  }

  patchValues(allPhoneNumbers: BusinessPhoneNumber[]) {
    this.formGroup.patchValue({ phoneNumbers: allPhoneNumbers });
  }

  verifyFormat(value: BusinessPhoneNumber[]) {
    if (value && value.length > 0) {
      value.forEach((x) => {
        if (x.phoneNumber) {
          x.phoneNumber = x.phoneNumber.replace(
            /^(\d{3})(\d{3})(\d{4}).*/,
            "($1)$2-$3"
          );
        }
      });
    }
    return value;
  }

  toggleRadio(phoneNumberCtrl: AbstractControl, index: number) {
    let value = !!phoneNumberCtrl.value.primary;
    value = !phoneNumberCtrl.value.primary;
    this.currentPhoneNumbers.controls.forEach((x) => (x.value.primary = false));
    phoneNumberCtrl.value.primary = value;
    this.currentPhoneNumbers.patchValue(this.currentPhoneNumbers.value);
  }

  verifyLengthLessThanFour() {
    return (
      this.currentPhoneNumbers.value.filter(
        (x: BusinessPhoneNumber) => !x.deleted
      ).length < 4
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/workforce/workforce-phone-info/workforce-phone-info.ts